import BottomBar from "@saleor/alternatedMaterial/components/BottomBarLogChange";
import LogChangeFilter from "@saleor/alternatedMaterial/components/LogChangeFilter";
import TableListLogChange from "@saleor/alternatedMaterial/components/TableListLogChange";
import { StatusFilter } from "@saleor/alternatedMaterial/types";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import {
  AlternatedMaterialFilterInput,
  AlternatedMaterialSortField,
  OrderDirection,
  useAlternatedMaterialsQuery,
  useExportLogChangeMutation
} from "@saleor/graphql";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";

const useStyles = makeStyles(
  () => ({
    boxContainer: {
      paddingBottom: "20rem"
    },
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        margin: 0,
        marginBottom: "1.2rem"
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    }
  }),
  { name: "LogChangeView" }
);

const LogChangeView = () => {
  const classes = useStyles();
  const [filter, setFilter] = useState<AlternatedMaterialFilterInput>({});
  const [generateDateTime, setGenerateDateTime] = useState<string>("");

  const { updateListSettings, settings } = useListSettings(
    ListViews.LOG_CHANGE
  );

  useEffect(() => {
    updateListSettings("rowNumber", defaultListSettings.LOG_CHANGE.rowNumber);
  }, []);

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      filter
    }),
    [settings?.rowNumber, valuesPaginationState, filter]
  );

  const listFilterArr: any[] =
    useMemo(() => Object.values(filter).filter(item => item), [filter]) || [];

  useEffect(() => {
    const generateTimeStr = moment().format("DD/MM/YYYY hh:mm");
    setGenerateDateTime(generateTimeStr);
  }, [listFilterArr]);

  const {
    data: dataLogChange,
    loading: loadingLogChange
  } = useAlternatedMaterialsQuery({
    variables: {
      ...queryVariables,
      sortBy: {
        field: AlternatedMaterialSortField.ORDER_NO,
        direction: OrderDirection.ASC
      }
    },
    displayLoader: true,
    skip: !listFilterArr.length
  });

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: dataLogChange?.alternatedMaterials?.latestPageItemNumber
    });
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    dataLogChange?.alternatedMaterials?.pageInfo,
    valuesPaginationState
  );

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };
  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const listDataLogChange = useMemo(
    () => mapEdgesToItems(dataLogChange?.alternatedMaterials) || [],
    [dataLogChange?.alternatedMaterials]
  );

  const statusFilter: StatusFilter = useMemo(() => {
    if (!listFilterArr.length || loadingLogChange) {
      return StatusFilter.INFO;
    } else if (listFilterArr.length && !listDataLogChange.length) {
      return StatusFilter.ERROR;
    } else if (listFilterArr.length && listDataLogChange.length) {
      return StatusFilter.SUCCESS;
    }
    return StatusFilter.INFO;
  }, [listDataLogChange, listFilterArr]);

  const [exportLogChangeMutation] = useExportLogChangeMutation();
  return (
    <>
      <Container className={classes.boxContainer}>
        <PageHeader
          className={classes.pageHeader}
          title={"Log Change  - Alternated Material"}
        />
        <LogChangeFilter
          setFilter={setFilter}
          statusFilter={statusFilter}
          dataFilter={listDataLogChange}
        />
        <TableListLogChange
          pageInfo={pageInfo}
          dataLogChange={listDataLogChange}
          latestPageItemNumber={
            dataLogChange?.alternatedMaterials?.latestPageItemNumber
          }
          totalCount={dataLogChange?.alternatedMaterials?.totalCount}
          disabled={loadingLogChange}
          listFilter={listFilterArr}
          onNextLastPage={handleNextLastPage}
          onUpdateListSettings={updateListSettings}
          onNextPage={loadNextPage}
          onPreviousPage={loadPreviousPage}
          onPreviousFirstPage={handlePreviousFirstPage}
          settings={settings}
          onRowClick={() => null}
        />
        {listDataLogChange.length && listFilterArr.length ? (
          <BottomBar
            dateStr={generateDateTime}
            onDownload={exportLogChangeMutation}
          />
        ) : (
          ""
        )}
      </Container>
    </>
  );
};

export default LogChangeView;
